'use strict';

import getProgramEpisodeId from './getProgramEpisodeId';
import CONTENTO_TYPES from '../domain/ContentoTypes';
import env from '../env';

// Helps calculate valid position
const SAVED_POSITION_RECAP_TIME = 5;
const MAX_ONGOING_ITEMS = 24;
const MAX_WATCHED_ITEMS = 1000;
const PERCENT_ENDED = 0.95;

export function createContentoHistory(continueWatchingItems) {
    let history = [];

    if (continueWatchingItems && continueWatchingItems.size !== 0) {
        history = continueWatchingItems
            .toJS()
            .filter((item) => !item.deleted)
            .slice(0, env.HISTORY_UPPER_LIMIT)
            .map((continueWatchingItem) => ({
                id: continueWatchingItem.videoId,
                progressSeconds: parseInt(continueWatchingItem.position, 10) || 0,
            }));
    }

    return history;
}

export function filterValid(items) {
    return (items || []).reduce((result, position) => {
        if (position.timestamp) {
            result.push(position);
        }

        return result;
    }, []);
}

const _sortByDate = (a, b) => {
    return b.timestamp - a.timestamp;
};

function setEndTime(position, videoLength, itemToSave) {
    const ended = position / videoLength > PERCENT_ENDED;

    if (ended) {
        itemToSave.ended = true;
        itemToSave.position = videoLength;
    } else {
        itemToSave.ended = false;

        // Move position 5 seconds earlier for better user experience
        itemToSave.position = position > SAVED_POSITION_RECAP_TIME ? position - SAVED_POSITION_RECAP_TIME : 0;
    }
}

function injectItemToSave(items, itemToSave) {
    const indexOfItem = items.findIndex((item) => item.videoId === itemToSave.videoId);

    if (indexOfItem > -1) {
        // Position already saved for this video
        items.splice(indexOfItem, 1);
    }
    items.push(itemToSave);
}

function resetLastViewedInTitle(items, itemToSave) {
    items
        .filter(
            (item) => item.titleArticleId && item.titleArticleId === itemToSave.titleArticleId && !item.movie
        )
        .forEach((itemInTitle) => (itemInTitle.lastViewedInTitle = false));
}

export function addPosition(barn, items, visionId, titleArticleId, __typename, position, videoLength) {
    const isMovie = __typename === CONTENTO_TYPES.SINGLE;

    let itemToSave = {
        barn: barn,
        timestamp: new Date().getTime(),
        videoId: visionId,
        lastViewedInTitle: true,
        titleArticleId: titleArticleId,
        movie: isMovie,
    };
    setEndTime(position, videoLength, itemToSave);

    resetLastViewedInTitle(items, itemToSave);
    injectItemToSave(items, itemToSave);

    // Only save x number of ongoing videos
    let ongoing = items.filter((item) => item.ended === false).sort(_sortByDate);
    ongoing = ongoing.slice(0, MAX_ONGOING_ITEMS);

    // Limit the number of watched videos
    let watched = items.filter((item) => item.ended === true).sort(_sortByDate);
    watched = watched.slice(0, MAX_WATCHED_ITEMS);

    return ongoing.concat(watched).sort(_sortByDate);
}

export function removeItems(items, itemsToRemove) {
    return items.map((item) => {
        // Direct hit
        if (
            itemsToRemove.find(
                (itemToRemove) =>
                    getProgramEpisodeId(itemToRemove.videoId) === getProgramEpisodeId(item.videoId)
            )
        ) {
            return { ...item, deleted: true };
        }

        if (!item.titleArticleId || item.movie) {
            return item;
        }

        if (
            itemsToRemove.find(
                (itemToRemove) => String(itemToRemove.titleArticleId) === String(item.titleArticleId)
            )
        ) {
            return { ...item, deleted: true };
        }

        return item;
    });
}

/**
 * Matches a vision id that look like 1393604-002A with continue watching items
 * video ids that look like either 1393604-002A (post contento), or 1393604-002 (pre contento).
 *
 * @param {array} continueWatchingItems The continue watching items.
 * @param {string} visionId The vision id to use for comparison.
 * @return {object|undefined} The continue watching item found, if any.
 */
export const findContinueWatchingItemForVisionId = (continueWatchingItems, visionId) => {
    const exactMatch = continueWatchingItems.find((item) => {
        return item.videoId === visionId;
    });

    return (
        exactMatch ||
        continueWatchingItems.find((item) => {
            return visionId.startsWith(item.videoId);
        })
    );
};
